// Shared SEO helpers for vue-meta metaInfo() across the SPA.
//
// Note on AI / non-JS crawlers: these helpers run client-side via vue-meta, so
// only JS-rendering crawlers (Google, Bing) see what they produce. AI agents
// such as GPTBot, ClaudeBot, and PerplexityBot generally do NOT execute JS and
// see only the static shell (public/index.html). Brand-level structured data
// for those bots lives statically in the shell; see
// docs/seo/ai-agent-discoverability.md.

const DEFAULT_ORIGIN = "https://invown.com";

export function canonicalOrigin() {
  return process.env.VUE_APP_CANONICAL_ORIGIN || DEFAULT_ORIGIN;
}

// Absolute URL for an /app route path (e.g. "/pitch/acme" -> ".../app/pitch/acme").
export function appUrl(fullPath) {
  return canonicalOrigin() + "/app" + (fullPath || "/");
}

// Strip HTML/markup and collapse whitespace, then truncate on a word boundary.
// Meta descriptions render best at ~155 chars; longer ones get truncated by
// search engines mid-word.
export function metaDescription(raw, maxLen = 155) {
  if (!raw) return "";
  const text = String(raw)
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&[a-z]+;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (text.length <= maxLen) return text;
  const clipped = text.slice(0, maxLen);
  const lastSpace = clipped.lastIndexOf(" ");
  return (lastSpace > 60 ? clipped.slice(0, lastSpace) : clipped).trim() + "…";
}

// Open Graph + Twitter Card tags. Every entry carries a vmid so a page-level
// metaInfo() overrides the App.vue defaults instead of producing duplicate tags.
export function socialMeta({ title, description, image, url, type = "website" } = {}) {
  const tags = [];
  const push = (key, attr, content) => {
    if (content) tags.push({ vmid: key, [attr]: key, content });
  };
  push("og:title", "property", title);
  push("og:description", "property", description);
  push("og:type", "property", type);
  push("og:url", "property", url);
  push("og:image", "property", image);
  push("twitter:card", "name", image ? "summary_large_image" : "summary");
  push("twitter:title", "name", title);
  push("twitter:description", "name", description);
  push("twitter:image", "name", image);
  return tags;
}

// schema.org structured data for a single offering, suitable for vue-meta's
// script[].json. Only fields safe to expose publicly are included.
export function offeringJsonLd(home, url) {
  if (!home) return null;
  const info = home.info || {};
  const address = info.address || {};
  const name =
    address.formatted_street_address ||
    address.street_name ||
    info.name ||
    "Invown offering";
  const json = {
    "@context": "https://schema.org",
    "@type": "Product",
    name,
    description: metaDescription(home.about_home, 300),
    url,
    brand: { "@type": "Organization", name: "Invown" },
  };
  if (Array.isArray(home.images) && home.images.length && home.images[0].url) {
    json.image = home.images[0].url;
  }
  if (address.city || address.state) {
    json.category = [address.city, address.state].filter(Boolean).join(", ");
  }
  return json;
}
